import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';

type StyleConsultationDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const StyleConsultationDetailScreen = () => {
  useNavigation<StyleConsultationDetailNavigationProp>();
  const { colors } = useTheme();
  useSafeAreaInsets();

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <LinearGradient
          colors={[palette.mediumViolet, palette.brightViolet]}
          style={styles.heroSection}
        >
          <View style={styles.heroContent}>
            <Ionicons
              name="people-outline"
              size={80}
              color="#FFFFFF"
              style={styles.heroIcon}
            />
            <Text style={styles.heroTitle}>Expert Style Consultation</Text>
            <Text style={styles.heroSubtitle}>
              Get personalized style advice from professional color consultants
              and stylists
            </Text>
            <View style={styles.comingSoonBadge}>
              <Text style={styles.comingSoonText}>Coming Soon</Text>
            </View>
          </View>
        </LinearGradient>

        {/* Preview Section */}
        <View style={styles.previewSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Professional Guidance
          </Text>
          <Image
            source={{ uri: 'https://picsum.photos/400/300?random=14' }}
            style={styles.previewImage}
            resizeMode="cover"
          />
          <Text
            style={[styles.previewDescription, { color: colors.secondary }]}
          >
            Connect with certified color consultants and style experts for
            personalized advice and professional guidance.
          </Text>
        </View>

        {/* Features Preview */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Planned Features
          </Text>
          <View style={styles.featuresList}>
            <View style={styles.featureItem}>
              <Ionicons name="videocam-outline" size={24} color="#8B5CF6" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                One-on-one video consultations
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="person-outline" size={24} color="#8B5CF6" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Certified style experts
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="clipboard-outline" size={24} color="#8B5CF6" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Personalized style plans
              </Text>
            </View>
            <View style={styles.featureItem}>
              <Ionicons name="refresh-outline" size={24} color="#8B5CF6" />
              <Text style={[styles.featureText, { color: colors.text }]}>
                Follow-up sessions
              </Text>
            </View>
          </View>
        </View>

        {/* Notify Section */}
        <View style={styles.notifySection}>
          <LinearGradient
            colors={[palette.mediumViolet, palette.brightViolet]}
            style={styles.notifyGradient}
          >
            <Text style={styles.notifyTitle}>Book Your Consultation</Text>
            <Text style={styles.notifySubtitle}>
              Be notified when expert style consultations become available
            </Text>
            <TouchableOpacity style={styles.notifyButton}>
              <Text style={styles.notifyButtonText}>Notify Me</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: 20,
    paddingVertical: 60,
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: 500,
    alignItems: 'center',
  },
  heroIcon: {
    marginBottom: 20,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: palette.white,
    textAlign: 'center',
    marginBottom: 16,
  },
  heroSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  comingSoonBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  comingSoonText: {
    color: palette.white,
    fontSize: 16,
    fontWeight: '600',
  },
  previewSection: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 24,
  },
  previewImage: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    marginBottom: 16,
  },
  previewDescription: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  section: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  featuresList: {
    gap: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  featureText: {
    fontSize: 16,
    flex: 1,
  },
  notifySection: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  notifyGradient: {
    borderRadius: 16,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  notifyTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: palette.white,
    textAlign: 'center',
    marginBottom: 8,
  },
  notifySubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 24,
  },
  notifyButton: {
    backgroundColor: palette.white,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
  },
  notifyButtonText: {
    color: palette.mediumViolet,
    fontSize: 16,
    fontWeight: '600',
  },
});

export default StyleConsultationDetailScreen;
